import type { RippleSource } from "@bfly/engine";
import { escapeHtml } from "./html.js";

/* The bio's shuffling phrases. The home bio is a fixed sentence with six blanks, and each blank
   (a slot) draws from its own pool. Some entries are "wry", and a sentence never shows more
   than one of those at a time. Every entry fits its spot in the sentence and stays under about
   28 characters, so the bio stays around four lines on a desktop. The RNG is seeded, so a seed
   always gives the same sentence.

   Each slot remembers its last few entries (the current one too) and a reroll skips them, so a
   slot doesn't repeat itself right away.

   Two things reroll a slot:
   - A wave picks a random handful of slots (at least 2, more for a bigger wave) and starts a
     new seed. Each picked slot rerolls when the wave's front reaches it, so the phrases change
     as the wave goes by.
   - A click on a slot rerolls just that one.
   Both go through `repick()`, so the one-wry-entry rule always holds.

   This file is just the logic; elements/jrsm-intro-copy.ts handles the page. */

interface Entry {
  t: string;
  /** A wry, tongue-in-cheek entry. A sentence shows at most one. */
  w?: boolean;
}

export const SLOTS = ["span", "pro", "jan", "art", "churn", "constant"] as const;
export type SlotKey = (typeof SLOTS)[number];

/** A slot's pool. Index 0 is the default line, so a pool is never empty. */
type Pool = readonly [Entry, ...Entry[]];

// The slot pools. Index 0 of each is the default line, and together with `TEMPLATE` those make
// the static bio that the build writes into index.html (`renderIntroCopyHtml`). The constant
// slot starts a sentence, so its entries start with a capital.
export const POOLS: Readonly<Record<SlotKey, Pool>> = {
  span: [
    { t: "front to back" },
    { t: "client to server" },
    { t: "schema to stylesheet" },
    { t: "bare metal to browser" },
    { t: "kernel to keyframe" },
    { t: "DNS to DOM" },
    { t: "packet to pixel" },
    { t: "SQL to SVG" },
    { t: "database to display" },
    { t: "socket to shader" },
    { t: "API to animation" },
    { t: "migration to modal" },
    { t: "container to CSS" },
    { t: "terminal to touchscreen" },
    { t: "byte to button" },
    { t: "load balancer to layout" },
    { t: "index to interface" },
    { t: "compiler to cursor" },
    { t: "wire to window" },
    { t: "cache to click" },
    { t: "404 to 200", w: true },
    { t: "localhost to prod", w: true },
    { t: "null to NaN", w: true },
  ],
  pro: [
    { t: "lead teams" },
    { t: "design architecture" },
    { t: "mentor juniors" },
    { t: "ship roadmaps" },
    { t: "review PRs" },
    { t: "set technical direction" },
    { t: "write the RFCs" },
    { t: "run design reviews" },
    { t: "plan migrations" },
    { t: "scope big projects" },
    { t: "unblock teams" },
    { t: "pair on hard bugs" },
    { t: "own production" },
    { t: "build platforms" },
    { t: "build developer tooling" },
    { t: "keep systems running" },
    { t: "draw boxes and arrows", w: true },
    { t: "say no to scope creep", w: true },
    { t: 'say "it depends"', w: true },
  ],
  jan: [
    { t: "untangle legacy systems" },
    { t: "exorcise tech debt" },
    { t: "resurrect dead services" },
    { t: "debug the deploy pipeline" },
    { t: "refactor old monoliths" },
    { t: "migrate ancient databases" },
    { t: "retire dead code" },
    { t: "trace flaky tests" },
    { t: "fix slow queries" },
    { t: "patch old dependencies" },
    { t: "map undocumented APIs" },
    { t: "tame config sprawl" },
    { t: "shrink build times" },
    { t: "chase memory leaks" },
    { t: "bisect regressions" },
    { t: "document tribal knowledge" },
    { t: "read cron logs", w: true },
    { t: "delete more than I write", w: true },
    { t: "name things", w: true },
    { t: "outlive the frameworks", w: true },
  ],
  art: [
    { t: "sketch in the margins" },
    { t: "chase generative rabbit holes" },
    { t: "write shaders at midnight" },
    { t: "draw with code" },
    { t: "plot strange attractors" },
    { t: "simulate flip-dot signs" },
    { t: "halftone old photos" },
    { t: "animate type for fun" },
    { t: "tune easing curves" },
    { t: "grow particle gardens" },
    { t: "fill sketchbooks" },
    { t: "prototype odd interfaces" },
    { t: "paint with noise functions" },
    { t: "chase the butterfly effect" },
    { t: "make art from math" },
    { t: "tile planes with Truchet", w: true },
    { t: "dither everything", w: true },
    { t: "code my own screensavers", w: true },
    { t: "fiddle with variable fonts", w: true },
  ],
  churn: [
    { t: "changing" },
    { t: "breaking" },
    { t: "shifting" },
    { t: "evolving" },
    { t: "moving" },
    { t: "updating" },
    { t: "multiplying" },
    { t: "forking" },
    { t: "turning over" },
    { t: "shipping major versions" },
    { t: "deprecating themselves", w: true },
    { t: "getting rewritten in Rust", w: true },
    { t: "getting supply-chained", w: true },
    { t: "rebranding", w: true },
    { t: "getting acquired", w: true },
    { t: "changing their licenses", w: true },
    { t: "adding AI to everything", w: true },
    { t: "growing new config flags", w: true },
  ],
  constant: [
    { t: "Adapting" },
    { t: "Curiosity" },
    { t: "Caring" },
    { t: "Reading the docs" },
    { t: "Learning" },
    { t: "Craft" },
    { t: "Clarity" },
    { t: "Taste" },
    { t: "Patience" },
    { t: "Listening" },
    { t: "Simplicity" },
    { t: "Empathy" },
    { t: "Testing" },
    { t: "Good naming" },
    { t: "Writing it down" },
    { t: "Shipping anyway", w: true },
    { t: "Caffeine", w: true },
    { t: "TCP", w: true },
    { t: "The backlog", w: true },
  ],
};

/** The sentence: the fixed bits of text, with the slots in between, in order. */
const TEMPLATE: ReadonlyArray<string | { slot: SlotKey }> = [
  "Two decades across the stack, ",
  { slot: "span" },
  ". I ",
  { slot: "pro" },
  ", ",
  { slot: "jan" },
  ", and still ",
  { slot: "art" },
  ". The tools keep ",
  { slot: "churn" },
  " underneath. ",
  { slot: "constant" },
  " is the constant.",
];

export type Picks = Record<SlotKey, Entry>;

export function isSlotKey(value: string | undefined): value is SlotKey {
  return SLOTS.some((slot) => slot === value);
}

/** The default bio as HTML: plain text with one span per slot. The static page ships it, so
    you get the same sentence without scripts (and so do crawlers). The bio element
    (`<jrsm-intro-copy>`) turns each slot into a button. */
export function renderIntroCopyHtml(): string {
  return TEMPLATE.map((segment) => {
    if (typeof segment === "string") return escapeHtml(segment);
    const text = POOLS[segment.slot][0]?.t ?? "";
    return `<span class="slot" data-slot="${segment.slot}">${escapeHtml(text)}</span>`;
  }).join("");
}

/** The bio with every slot's longest entry, as plain text. The bio keeps this text's height,
    so a rewrite never shifts the page. */
export function longestIntroCopy(): string {
  return TEMPLATE.map((segment) =>
    typeof segment === "string"
      ? segment
      : POOLS[segment.slot].reduce((long, entry) => (entry.t.length > long.t.length ? entry : long))
          .t,
  ).join("");
}

/** The next seed, for when a wave rerolls the bio. */
export function nextSeed(seed: number): number {
  return (Math.imul(seed, 0x9e3779b9) + 0x85ebca6b) >>> 0;
}

/** How many of a slot's recent entries a reroll skips, counting the current one. */
const RECENT_LIMIT = 5;

/** Each slot's recent texts, oldest first. The last one is what's showing now. */
export type Recent = Record<SlotKey, string[]>;

/** A fresh history with only what `picks` shows. */
export function recentFrom(picks: Picks): Recent {
  return {
    span: [picks.span.t],
    pro: [picks.pro.t],
    jan: [picks.jan.t],
    art: [picks.art.t],
    churn: [picks.churn.t],
    constant: [picks.constant.t],
  };
}

/** Notes that `slot` now shows `text`: it moves to the end, and the oldest fall off. */
export function remember(recent: Recent, slot: SlotKey, text: string): void {
  recent[slot] = [...recent[slot].filter((t) => t !== text), text].slice(-RECENT_LIMIT);
}

interface PickOpts {
  straightOnly?: boolean; // exclude wry entries
  /** Recent texts to skip, oldest first. The last one is what's showing now. */
  exclude?: readonly string[];
}

/** Picks one entry from a pool, within the filters. If the history covers every candidate, it
    still skips the text that's showing (a reroll should change something). It always returns
    an entry. */
export function pick(pool: readonly Entry[], rng: () => number, opts: PickOpts = {}): Entry {
  let p: readonly Entry[] = opts.straightOnly ? pool.filter((e) => !e.w) : pool;
  if (p.length === 0) p = pool;
  const avoid = opts.exclude ?? [];
  let fresh: readonly Entry[] = p.filter((e) => !avoid.includes(e.t));
  if (fresh.length === 0) fresh = p.filter((e) => e.t !== avoid.at(-1));
  if (fresh.length === 0) fresh = p;
  return fresh[Math.floor(rng() * fresh.length)]!;
}

/** The slots (besides `except`) that show a wry entry right now. */
export function wrySlots(picks: Picks, except?: SlotKey): SlotKey[] {
  return SLOTS.filter((s) => s !== except && picks[s]?.w);
}

/** The default (index 0) picks, which match the static bio in index.html. */
export function canonicalPicks(): Picks {
  return {
    span: POOLS.span[0],
    pro: POOLS.pro[0],
    jan: POOLS.jan[0],
    art: POOLS.art[0],
    churn: POOLS.churn[0],
    constant: POOLS.constant[0],
  };
}

/** How many phrases a wave rewrites: more for a higher level, from 2 up to every slot. A theme
    wave doesn't change the words at all. */
export function waveRewriteCount(source: RippleSource, level: number): number {
  if (source === "theme") return 0;
  return Math.max(2, Math.min(SLOTS.length, Math.round((level / 5) * SLOTS.length)));
}

/** Picks `count` different slots to reroll (clamped to 2 through all of them), with a partial
    Fisher-Yates shuffle. */
export function chooseRerollSlots(count: number, rng: () => number): SlotKey[] {
  const n = Math.max(2, Math.min(SLOTS.length, Math.floor(count)));
  const pool = [...SLOTS];
  for (let i = 0; i < n; i++) {
    const j = i + Math.floor(rng() * (pool.length - i));
    [pool[i]!, pool[j]!] = [pool[j]!, pool[i]!];
  }
  return pool.slice(0, n);
}

/** Rerolls one slot. The new entry skips the slot's recent texts, and it's a straight one if
    another slot already shows a wry one. It changes `picks` in place; the caller records the
    new text in `recent`. */
export function repick(picks: Picks, slot: SlotKey, rng: () => number, recent: Recent): void {
  const straightOnly = wrySlots(picks, slot).length >= 1;
  picks[slot] = pick(POOLS[slot], rng, { straightOnly, exclude: recent[slot] });
}

/** The waiting slots that their wave's front has reached, as [wave id, slot] pairs. A wave
    with no ring has ended, so all of its slots are due. */
export function dueRewrites<Ring extends { readonly id: number }>(
  pending: ReadonlyMap<number, ReadonlySet<SlotKey>>,
  rings: readonly Ring[],
  reached: (slot: SlotKey, ring: Ring) => boolean,
): [number, SlotKey][] {
  const due: [number, SlotKey][] = [];
  for (const [id, slots] of pending) {
    const ring = rings.find((candidate) => candidate.id === id);
    for (const slot of slots) {
      if (!ring || reached(slot, ring)) due.push([id, slot]);
    }
  }
  return due;
}
